'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface JobSortControlsProps {
  onSortChange: (sortBy: string, sortOrder: string) => void;
  totalJobs: number;
  onToggleFilters?: () => void;
  showMobileFilters?: boolean;
}

const JobSortControls = ({ onSortChange, totalJobs, onToggleFilters, showMobileFilters = false }: JobSortControlsProps) => {
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const sortOptions = [
    { value: 'date', label: 'Date Posted' },
    { value: 'salary', label: 'Salary' },
    { value: 'location', label: 'Location' }
  ];

  const handleSortChange = (newSortBy: string) => {
    let newSortOrder = 'desc';
    
    // If clicking the same sort option, toggle order
    if (newSortBy === sortBy) {
      newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    } else {
      // Default order for different sort types
      if (newSortBy === 'location') {
        newSortOrder = 'asc';
      }
    }
    
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    onSortChange(newSortBy, newSortOrder);
  };

  const getSortIcon = (optionValue: string) => {
    if (sortBy !== optionValue) return null;
    return sortOrder === 'desc' ? 'ChevronDownIcon' : 'ChevronUpIcon';
  };

  return (
    <div className="flex items-center justify-between bg-card border border-border rounded-industrial p-4 industrial-shadow">
      <div className="flex items-center space-x-4">
        {/* Mobile Filter Toggle */}
        {showMobileFilters && (
          <button
            onClick={onToggleFilters}
            className="lg:hidden flex items-center space-x-2 px-4 py-2 border border-border rounded-industrial hover:bg-muted transition-industrial"
          >
            <Icon name="AdjustmentsHorizontalIcon" size={20} />
            <span className="font-heading uppercase tracking-wide text-sm">Filters</span>
          </button>
        )}

        {/* Results Count */}
        <div className="flex items-center space-x-2">
          <Icon name="BriefcaseIcon" size={20} className="text-text-secondary" />
          <span className="font-heading text-sm uppercase tracking-wide">
            <span className="font-bold text-foreground">{totalJobs}</span> Jobs Found
          </span>
        </div>
      </div>

      {/* Sort Controls */}
      <div className="flex items-center space-x-4">
        <span className="font-heading text-sm uppercase tracking-wide text-text-secondary hidden sm:block">
          Sort by:
        </span>
        
        <div className="flex items-center space-x-2">
          {sortOptions.map((option) => {
            const isActive = sortBy === option.value;
            const icon = getSortIcon(option.value);
            
            return (
              <button
                key={option.value}
                onClick={() => handleSortChange(option.value)}
                className={`flex items-center space-x-1 px-3 py-2 rounded-industrial transition-industrial font-heading text-sm uppercase tracking-wide ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border hover:bg-muted'
                }`}
              >
                <span className="hidden sm:inline">{option.label}</span>
                <span className="sm:hidden">
                  {option.label.split(' ')[0]}
                </span>
                {icon && <Icon name={icon as any} size={14} />}
              </button>
            );
          })}
        </div>

        {/* View Toggle (Future Enhancement) */}
        <div className="hidden md:flex items-center space-x-1 border border-border rounded-industrial p-1">
          <button className="p-2 bg-primary text-primary-foreground rounded-industrial">
            <Icon name="Bars3Icon" size={16} />
          </button>
          <button className="p-2 hover:bg-muted rounded-industrial transition-industrial">
            <Icon name="Squares2X2Icon" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobSortControls;